import type { Call } from '@twilio/voice-sdk';
import type { Dispatch } from 'redux';

import {
  claimStarted, claimWon, claimLost, callEnded, muteToggled, ringDismissed, RingPayload,
} from '@/redux/app/actions/calls';
import { avo } from '@/api/app/avo-client';
import * as Twilio from './twilio';

// The live Twilio Call is not serializable, so it lives here instead of Redux;
// components read display state from the calls slice and drive the call
// through these helpers.
let activeCall: Call | null = null;

export const getActiveCall = () => activeCall;

// Mirrors Calls::RingService::CLAIM_TTL: once it passes without the agent leg
// joining, the claim watchdog releases the claim and re-rings everyone, so
// there is nothing to keep waiting for.
export const JOIN_TIMEOUT_MS = 20_000;

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error(`Timed out joining the call after ${ms}ms`)), ms);
  promise.then(
    (value) => { clearTimeout(timer); resolve(value); },
    (err) => { clearTimeout(timer); reject(err); },
  );
});

interface ClaimResponse {
  won: boolean;
  call_id?: string;
  conference_name?: string;
  join_token?: string;
}

// Claim a ringing call and, if we win, join its conference. Returns true when
// this browser is now on the call.
export const answerCall = async (subdomain: string, ring: RingPayload, dispatch: Dispatch): Promise<boolean> => {
  // Winning the claim while on another call would take the ring away from every
  // other agent for nothing: joining refuses rather than drop the current call
  if (Twilio.isOnCall()) return false;
  dispatch(claimStarted({ callId: ring.call_id }));

  let claim: ClaimResponse;
  try {
    const result = await avo.ajax<ClaimResponse>({
      method: 'POST',
      route: `/v1/calls/${ring.call_id}/claim`,
      payload: { subdomain },
    }, { retries: 0 });
    claim = result.data;
  } catch (err) {
    console.error(err);
    dispatch(claimLost({ callId: ring.call_id }));
    return false;
  }

  if (!claim.won) {
    dispatch(claimLost({ callId: ring.call_id }));
    return false;
  }

  // Joining can reject (mic permission denied, no input device, device
  // unregistered) or hang (permission dialog left open, ICE never completing).
  // Either way the claim must resolve, or the prompt stays stuck on
  // "Connecting…" and every later ring stacks underneath it.
  let call: Call | null;
  const join = Twilio.joinConference(claim.conference_name, claim.join_token);
  try {
    call = await withTimeout(join, JOIN_TIMEOUT_MS);
  } catch (err) {
    console.error(err);
    // Free the device for later rings; a join that completes after we gave up
    // would be a live leg with no UI
    Twilio.abandonJoin();
    join.then((late) => late?.disconnect()).catch(() => {});
    dispatch(claimLost({ callId: ring.call_id }));
    return false;
  }
  if (!call) {
    // Device wasn't ready; the claim watchdog re-rings everyone shortly
    dispatch(claimLost({ callId: ring.call_id }));
    return false;
  }

  activeCall = call;
  const onEnded = () => {
    if (activeCall === call) activeCall = null;
    dispatch(callEnded({ callId: ring.call_id }));
  };
  call.on('disconnect', onEnded);
  call.on('cancel', onEnded);
  call.on('error', onEnded);

  dispatch(claimWon({
    callId: ring.call_id,
    contactName: ring.contact_name,
    contactPhone: ring.contact_phone,
  }));
  return true;
};

// Rings this tab told the server about, and ones its agent declined
const acknowledged = new Set<string>();
const declined = new Set<string>();

const ringOpen = (ring: RingPayload) => ring.started_at + ring.timeout_seconds > Date.now() / 1000;

const postRing = (subdomain: string, callId: string, action: 'ringing' | 'decline') => avo.ajax({
  method: 'POST',
  route: `/v1/calls/${callId}/${action}`,
  payload: { subdomain },
}).catch((err) => console.error(err));

// Tell the server this browser can answer the ring, like a registered Twilio
// Device on legacy /voice: until every agent who got it declines (and the
// phone legs end), the caller is not sent to voicemail before the deadline.
// A device still registering after a page load, or an agent on another call,
// confirms once it can answer, if the ring is still open by then.
export const acknowledgeRing = (subdomain: string, ring: RingPayload) => {
  const callId = ring.call_id;
  if (acknowledged.has(callId) || declined.has(callId)) return;
  const send = () => {
    acknowledged.add(callId);
    postRing(subdomain, callId, 'ringing');
  };
  if (Twilio.canAnswerCalls()) {
    send();
    return;
  }
  const listener = () => {
    if (acknowledged.has(callId) || declined.has(callId) || !ringOpen(ring)) {
      Twilio.status.removeListener(listener);
    } else if (Twilio.canAnswerCalls()) {
      Twilio.status.removeListener(listener);
      send();
    }
  };
  Twilio.status.addListener(listener);
};

// The agent's other tabs learn about a decline from the server (a call_ring_stop
// with reason 'declined'); a later re-ring of the call must not prompt them again
export const markDeclined = (callId: string) => {
  declined.add(callId);
};

export const isDeclined = (callId: string) => declined.has(callId);

export const declineCall = (subdomain: string, ring: RingPayload, dispatch: Dispatch) => {
  markDeclined(ring.call_id);
  dispatch(ringDismissed({ callId: ring.call_id }));
  postRing(subdomain, ring.call_id, 'decline');
};

export const hangupActiveCall = () => {
  activeCall?.disconnect();
};

export const toggleMute = (dispatch: Dispatch) => {
  if (!activeCall) return;
  const muted = !activeCall.isMuted();
  activeCall.mute(muted);
  dispatch(muteToggled({ muted }));
};

export const sendDigits = (digits: string) => {
  activeCall?.sendDigits(digits);
};
